import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaBuilding, FaUserPlus, FaHourglassHalf, FaCheckCircle, FaCross, FaExclamationCircle, FaRegStopCircle, FaDoorClosed, FaRegClosedCaptioning, FaXbox, FaRemoveFormat, FaRegFlushed } from 'react-icons/fa';
import RegisterAgencyForm from '../../components/agency/RegisterAgencyForm';
import JoinAgencyPage from './JoinAgencyPage';
import { agencyService } from '../../services/agencyService';
import { authService } from '../../services/ParseService';
import AgencyApplication from '../../models/AgencyApplication';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const AgencyOptionsPage = () => {
  const [selectedOption, setSelectedOption] = useState(null); // 'register' or 'join'
  const [pendingApplication, setPendingApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const checkPendingApplication = async () => {
      try {
        // Explicitly check if user is authenticated
        const user = authService.getCurrentUser();
        if (!user) {
          navigate('/');
          return;
        }

        const pending = await agencyService.checkPendingApplication(user);
        setPendingApplication(pending);
      } catch (error) {
        console.error('Error checking pending application:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    // Only check if there's a current user
    if (currentUser) {
      checkPendingApplication();
    } else {
      navigate('/');
    }
  }, [navigate, currentUser]);

  // Handler for successful agency application submission
  const handleApplicationSubmitted = async (report) => {
    try {
      // Wait a moment for the server to create the AgencyApplication
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Get the latest application data
      const user = authService.getCurrentUser();
      const latestApplication = await agencyService.checkPendingApplication(user);
      
      if (latestApplication) {
        setPendingApplication(latestApplication);
        setApplicationSubmitted(true);
      } else {
        // If we can't find the application yet, try again after a short delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retryApplication = await agencyService.checkPendingApplication(user);
        setPendingApplication(retryApplication);
        setApplicationSubmitted(true);
      }
    } catch (error) {
      console.error('Error getting latest application data:', error);
      // Still set as submitted even if we can't get the latest data
      setApplicationSubmitted(true);
    }
  };

  const SuccessContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    padding: 2rem;
  `;

  const SuccessIcon = styled.div`
    color: #4CAF50;
    font-size: 5rem;
    margin-bottom: 1rem;
  `;

  const SuccessMessage = styled.p`
    font-size: 1.2rem;
    margin-bottom: 1rem;
    color: #333;
  `;

  // If application is submitted, show success view
  if (applicationSubmitted) {
    return (
      <Container>
        <PendingCard>
          <IconWrapper>
            <FaCheckCircle />
          </IconWrapper>
          <ApplicationDetails>
            <h2>{t('agencyOptions.applicationSubmitted')}</h2>
            <p>{t('agencyOptions.applicationSubmittedMessage')}</p>
            <p>{t('agencyOptions.applicationReviewMessage')}</p>
            <h3>{t('agencyOptions.applicationDetails')}</h3>
            <ul>
              <li><strong>{t('agencyOptions.agencyName')}:</strong> {pendingApplication?.agencyName || 'N/A'}</li>
              <li><strong>{t('agencyOptions.contactEmail')}:</strong> {pendingApplication?.contactEmail || 'N/A'}</li>
              <li><strong>{t('agencyOptions.submitted')}:</strong> {pendingApplication?.createdAt?.toLocaleDateString() || 'N/A'}</li>
            </ul>
            <Button onClick={() => window.location.reload()}>{t('agencyOptions.goToDashboard')}</Button>
          </ApplicationDetails>
        </PendingCard>
      </Container>
    );
  }

  // If there's a pending application, show appropriate status
  if (pendingApplication) {
    const status = pendingApplication.get(AgencyApplication.keys.STATUS);
    
    if (status === AgencyApplication.status.PENDING) {
      return (
        <Container>
          <PendingCard>
            <IconWrapper>
              <FaHourglassHalf />
            </IconWrapper>
            <ApplicationDetails>
              <h2>{t('agencyOptions.applicationInReview')}</h2>
              <p>{t('agencyOptions.applicationInReviewMessage')}</p>
              <p>{t('agencyOptions.applicationReviewMessage')}</p>
              <h3>{t('agencyOptions.applicationDetails')}</h3>
              <ul>
                <li><strong>{t('agencyOptions.agencyName')}:</strong> {pendingApplication.get(AgencyApplication.keys.AGENCY_NAME)}</li>
                <li><strong>{t('agencyOptions.contactEmail')}:</strong> {pendingApplication.get(AgencyApplication.keys.CONTACT_EMAIL)}</li>
                <li><strong>{t('agencyOptions.submitted')}:</strong> {pendingApplication.createdAt.toLocaleDateString()}</li>
              </ul>
            </ApplicationDetails>
          </PendingCard>
        </Container>
      );
    }
    
    if (status === AgencyApplication.status.APPROVED) {
      navigate('/dashboard');
      return null;
    }
    
    if (status === AgencyApplication.status.REFUSED) {
      return (
        <Container>
          <PendingCard>
            <IconWrapper>
              <FaRegFlushed style={{ color: 'red' }}/>
            </IconWrapper>
            <ApplicationDetails>
              <h2>{t('agencyOptions.applicationRefused')}</h2>
              <p>{t('agencyOptions.applicationRefusedMessage')}</p>
              {pendingApplication.get(AgencyApplication.keys.MESSAGE) && (
                <div>
                  <h3>{t('agencyOptions.adminMessage')}</h3>
                  <p style={{ color: 'red', fontStyle: 'italic' }}>
                    {pendingApplication.get(AgencyApplication.keys.MESSAGE)}
                  </p>
                </div>
              )}
              <Button onClick={() => setSelectedOption('register')}>{t('agencyOptions.tryAgain')}</Button>
            </ApplicationDetails>
          </PendingCard>
        </Container>
      );
    }
  }

  if (selectedOption === 'register') {
    return (
      <Container>
        {!applicationSubmitted && (
          <BackButton onClick={() => setSelectedOption(null)}>{t('agencyOptions.backToOptions')}</BackButton>
        )}
        <RegisterAgencyForm 
          onSuccessfulSubmission={handleApplicationSubmitted} 
        />
      </Container>
    );
  }

  if (selectedOption === 'join') {
    return (
      <Container>
        {!applicationSubmitted && (
          <BackButton onClick={() => setSelectedOption(null)}>{t('agencyOptions.backToOptions')}</BackButton>
        )}
        <JoinAgencyPage />
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <LoadingText>{t('agencyOptions.loading')}</LoadingText>
      </Container>
    );
  }

  return (
    <Container>
      <h1>{t('agencyOptions.chooseYourPath')}</h1>
      <p>{t('agencyOptions.selectHowToProceed')}</p>

      <OptionsGrid>
        <OptionCard onClick={() => setSelectedOption('register')}>
          <IconWrapper>
            <FaBuilding />
          </IconWrapper>
          <h2>{t('agencyOptions.registerAgency')}</h2>
          <p>{t('agencyOptions.registerAgencyDescription')}</p>
          <ul>
            <li>{t('agencyOptions.recruitAndManageStreamers')}</li>
            <li>{t('agencyOptions.setCommissionRates')}</li>
            <li>{t('agencyOptions.accessAgencyDashboard')}</li>
          </ul>
          <Button>{t('agencyOptions.registerAgency')}</Button>
        </OptionCard>

        <OptionCard onClick={() => setSelectedOption('join')}>
          <IconWrapper>
            <FaUserPlus />
          </IconWrapper>
          <h2>{t('agencyOptions.joinAgency')}</h2>
          <p>{t('agencyOptions.joinAgencyDescription')}</p>
          <ul>
            <li>{t('agencyOptions.findAgencyOpportunities')}</li>
            <li>{t('agencyOptions.exploreAgencyBenefits')}</li>
            <li>{t('agencyOptions.applyToAgencies')}</li>
          </ul>
          <Button>{t('agencyOptions.joinAgency')}</Button>
        </OptionCard>
      </OptionsGrid>
    </Container>
  );
};

const IconWrapper = styled.div`
  font-size: 3rem;
  color: #1a2a6c;
  margin-bottom: 1rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 2rem;

  h1 {
    color: #1a2a6c;
    margin-bottom: 1rem;
    text-align: center;
  }

  > p {
    text-align: center;
    color: #666;
    margin-bottom: 3rem;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #1a2a6c;
  cursor: pointer;
  font-weight: 600;
  padding: 0;
  margin-bottom: 2rem;

  &:hover {
    text-decoration: underline;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const OptionCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-5px);
  }

  h2 {
    color: #1a2a6c;
    margin: 1rem 0;
  }

  p {
    color: #666;
    margin-bottom: 1.5rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
    text-align: left;

    li {
      padding: 0.5rem 0;
      color: #666;
      position: relative;
      padding-left: 1.5rem;

      &:before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #28a745;
      }
    }
  }
`;

const LoadingText = styled.div`
  text-align: center;
  font-size: 1.2rem;
  color: #666;
`;

const PendingCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;

  h2 {
    color: #1a2a6c;
    margin: 1rem 0;
  }

  p {
    color: #666;
    margin: 0.5rem 0;
  }

  ${IconWrapper} {
    color: #f39c12;
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const ApplicationDetails = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
  text-align: left;

  h3 {
    color: #1a2a6c;
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  li {
    margin: 0.5rem 0;
    color: #666;

    strong {
      color: #1a2a6c;
      margin-right: 0.5rem;
    }
  }
`;

const Button = styled.button`
  background: #1a2a6c;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 1rem 2rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: background 0.2s;

  &:hover {
    background: #2a3a7c;
  }
`;

export default AgencyOptionsPage;
